import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import { useState } from 'react';
import { X } from 'lucide-react';
import { teamMembers, boardMembers, coaches } from '../data/siteData';


const AboutPage = () => {
  const { t, i18n } = useTranslation();
  const [selectedPlayer, setSelectedPlayer] = useState(null);

  // Formation 4-4-2: 1 GK, 4 DEF, 4 MID, 2 FWD
  const starters = teamMembers.filter(p => !p.is_reserve);
  const reserves = teamMembers.filter(p => p.is_reserve);
  const founderOrder = ['board_0', 'board_2', 'board_3', 'board_4', 'board_1', 'board_5', 'board_6', 'board_7'];
  const orderedBoardMembers = [...boardMembers].sort(
    (a, b) => founderOrder.indexOf(a.id) - founderOrder.indexOf(b.id)
  );
  
  const goalkeeper = starters.length >= 1 ? [starters[0]] : [];
  const defenders = starters.length >= 5 ? starters.slice(1, 5) : [];
  const midfielders = starters.length >= 9 ? starters.slice(5, 9) : [];
  const forwards = starters.length >= 11 ? starters.slice(9, 11) : [];

  const PlayerCard = ({ player }) => (
    <div 
      className="relative group cursor-pointer" 
      data-testid={`player-${player.id}`}
      onClick={() => setSelectedPlayer(player)}
    >
      <div className="w-16 h-16 sm:w-20 sm:h-20 md:w-24 md:h-24 lg:w-28 lg:h-28 mx-auto mb-2 overflow-hidden border-2 border-white/50 relative bg-white/20 rounded-full group-hover:border-white group-hover:scale-105 transition-all duration-300">
        <img
          src={player.image}
          alt={i18n.language === 'bg' ? player.name_bg : player.name}
          className="w-full h-full object-cover grayscale group-hover:grayscale-0 transition-all duration-500 group-hover:scale-110"
        />
        {player.number && (
          <div className="absolute -bottom-1 left-1/2 -translate-x-1/2 bg-white text-green-800 w-5 h-5 md:w-6 md:h-6 flex items-center justify-center font-bold text-[10px] md:text-xs rounded-full shadow-lg">
            {player.number}
          </div>
        )}
      </div>
      <div className="text-center">
        <h4 className="font-heading text-[10px] sm:text-xs md:text-sm font-semibold text-white drop-shadow-lg truncate px-1">
          {i18n.language === 'bg' ? player.name_bg : player.name}
        </h4>
        <p className="text-[8px] sm:text-[10px] md:text-xs text-white/80">
          {i18n.language === 'bg' ? player.position_bg : player.position}
        </p>
      </div>
    </div>
  );

  // Player Lightbox Modal - Premium Design
  const PlayerLightbox = ({ player, onClose }) => (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      {/* Animated background */}
      <motion.div 
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="absolute inset-0 bg-black/90 backdrop-blur-md"
      />
      
      {/* Floating particles effect */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {[...Array(20)].map((_, i) => (
          <motion.div
            key={i}
            className="absolute w-1 h-1 bg-accent/30 rounded-full"
            initial={{ 
              x: Math.random() * window.innerWidth, 
              y: window.innerHeight + 10,
              scale: Math.random() * 2 + 0.5
            }}
            animate={{ 
              y: -10,
              transition: { 
                duration: Math.random() * 10 + 10, 
                repeat: Infinity,
                ease: "linear",
                delay: Math.random() * 5
              }
            }}
          />
        ))}
      </div>

      <motion.div
        initial={{ scale: 0.8, opacity: 0, y: 50, rotateX: 15 }}
        animate={{ scale: 1, opacity: 1, y: 0, rotateX: 0 }}
        exit={{ scale: 0.8, opacity: 0, y: 50 }}
        transition={{ type: "spring", damping: 25, stiffness: 300 }}
        className="relative max-w-2xl w-full overflow-hidden"
        onClick={(e) => e.stopPropagation()}
        style={{ perspective: "1000px" }}
      >
        {/* Glowing border effect */}
        <div className="absolute -inset-1 bg-gradient-to-r from-accent via-green-500 to-accent rounded-lg opacity-75 blur-sm animate-pulse" />
        
        <div className="relative bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 rounded-lg overflow-hidden border border-white/10">
          {/* Close button */}
          <motion.button
            onClick={onClose}
            whileHover={{ scale: 1.1, rotate: 90 }}
            whileTap={{ scale: 0.9 }}
            className={`absolute top-4 ${player.memorial ? 'left-4' : 'right-4'} z-30 w-10 h-10 bg-white/10 hover:bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center transition-colors`}
          >
            <X className="w-5 h-5 text-white" />
          </motion.button>

          <div className="flex flex-col md:flex-row">
            {/* Image section with overlay effects */}
            <div className="w-full md:w-1/2 relative group">
              <div className="aspect-square relative overflow-hidden">
                {/* Gradient overlays */}
                <div className="absolute inset-0 bg-gradient-to-t from-slate-900 via-transparent to-transparent z-10" />
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-transparent to-slate-900/50 z-10" />
                
                <motion.img
                  initial={{ scale: 1.2 }}
                  animate={{ scale: 1 }}
                  transition={{ duration: 0.6 }}
                  src={player.image}
                  alt={i18n.language === 'bg' ? player.name_bg : player.name}
                  className={`w-full h-full object-cover ${player.memorial ? 'object-[82%_center]' : 'object-center'}`}
                />
                
                {/* Player number badge */}
                {player.number && (
                  <motion.div 
                    initial={{ scale: 0, rotate: -180 }}
                    animate={{ scale: 1, rotate: 0 }}
                    transition={{ delay: 0.3, type: "spring" }}
                    className="absolute bottom-4 left-4 z-20"
                  >
                    <div className="relative">
                      <div className="absolute inset-0 bg-accent blur-md opacity-50" />
                      <div className="relative bg-gradient-to-br from-accent to-green-600 text-white w-14 h-14 flex items-center justify-center font-bold text-2xl rounded-lg shadow-lg border border-white/20">
                        {player.number}
                      </div>
                    </div>
                  </motion.div>
                )}

                {/* Position badge */}
                <motion.div
                  initial={{ x: -50, opacity: 0 }}
                  animate={{ x: 0, opacity: 1 }}
                  transition={{ delay: 0.4 }}
                  className={`absolute top-4 ${player.memorial ? 'left-16' : 'left-4'} z-20 bg-white/10 backdrop-blur-md px-3 py-1 rounded-full border border-white/20`}
                >
                  <span className="text-white/90 text-sm font-medium uppercase tracking-wider">
                    {i18n.language === 'bg' ? player.position_bg : player.position}
                  </span>
                </motion.div>
              </div>
            </div>
            
            {/* Content section */}
            <div className="w-full md:w-1/2 p-8 flex flex-col justify-center relative">
              {/* Decorative line */}
              <motion.div 
                initial={{ scaleX: 0 }}
                animate={{ scaleX: 1 }}
                transition={{ delay: 0.5 }}
                className="absolute top-0 left-8 right-8 h-px bg-gradient-to-r from-transparent via-accent to-transparent"
              />
              
              <motion.div
                initial={{ y: 20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.3 }}
              >
                <h3 className="font-heading text-3xl md:text-4xl font-bold text-white mb-3 leading-tight">
                  {i18n.language === 'bg' ? player.name_bg : player.name}
                </h3>
                
                {/* Stats or achievements - only show World Cup for players with numbers */}
                <div className="flex gap-4 mb-6">
                  {player.number && (
                    <>
                      <div className="text-center">
                        <div className="text-2xl font-bold text-accent">'94</div>
                        <div className="text-xs text-white/50 uppercase tracking-wider">World Cup</div>
                      </div>
                      <div className="text-center">
                        <div className="text-2xl font-bold text-white">#{player.number}</div>
                        <div className="text-xs text-white/50 uppercase tracking-wider">{i18n.language === 'bg' ? 'Номер' : 'Number'}</div>
                      </div>
                    </>
                  )}
                </div>

                <motion.div 
                  initial={{ y: 20, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  transition={{ delay: 0.5 }}
                  className="relative"
                >
                  <div className="absolute -left-4 top-0 bottom-0 w-1 bg-gradient-to-b from-accent via-accent/50 to-transparent rounded-full" />
                  <p className="text-white/70 leading-relaxed pl-2">
                    {i18n.language === 'bg' ? player.bio_bg : player.bio}
                  </p>
                </motion.div>
              </motion.div>

              {/* Decorative footer - only for players */}
              {player.number && (
                <motion.div 
                  initial={{ y: 20, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  transition={{ delay: 0.6 }}
                  className="mt-8 pt-4 border-t border-white/10"
                >
                  <div className="flex items-center gap-2 text-white/40 text-sm">
                    <span className="w-2 h-2 bg-accent rounded-full animate-pulse" />
                    <span>{i18n.language === 'bg' ? 'Легенда на българския футбол' : 'Bulgarian Football Legend'}</span>
                  </div>
                </motion.div>
              )}
            </div>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );

  return (
    <div className="min-h-screen py-32">
      {/* Lightbox */}
      <AnimatePresence>
        {selectedPlayer && (
          <PlayerLightbox player={selectedPlayer} onClose={() => setSelectedPlayer(null)} />
        )}
      </AnimatePresence>

      <section className="bg-background py-20">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-20">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <h1 className="font-heading text-5xl md:text-6xl font-bold text-foreground mb-6" data-testid="about-title">
              {t('about.title')}
            </h1>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2, duration: 0.8 }}
            className="max-w-4xl mx-auto mb-20"
          >
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-foreground mb-6">
              {t('about.missionTitle')}
            </h2>
            <p className="text-lg text-muted-foreground leading-relaxed">
              {t('about.missionText')}
            </p>
          </motion.div>
        </div>
      </section>

      <section className="py-20 bg-muted">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-20">
          <motion.h2
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="font-heading text-4xl md:text-5xl font-bold text-foreground mb-4 text-center"
          >
            {i18n.language === 'bg' ? 'Основатели на Фондация "Национали 94"' : 'Founders of the Nationals 94 Foundation'}
          </motion.h2>
          <p className="text-center text-muted-foreground mb-12">
            {i18n.language === 'bg' ? 'Управителен съвет' : 'Board of Directors'}
          </p>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-8 max-w-5xl mx-auto">
            {orderedBoardMembers.map((member, index) => (
              <motion.div
                key={member.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1, duration: 0.6 }}
                className="group cursor-pointer"
                onClick={() => setSelectedPlayer(member)}
              >
                <div className="relative mb-4">
                  <div className="aspect-square overflow-hidden border-2 border-border bg-card group-hover:border-accent transition-all duration-300">
                    <img
                      src={member.image}
                      alt={i18n.language === 'bg' ? member.name_bg : member.name}
                      className={`w-full h-full object-cover group-hover:scale-110 transition-transform duration-500 ${member.memorial ? 'object-[82%_center]' : 'object-center'}`}
                    />
                  </div>
                </div>
                <h3 className="font-heading text-lg font-semibold text-foreground mb-1 group-hover:text-accent transition-colors">
                  {i18n.language === 'bg' ? member.name_bg : member.name}
                </h3>
                <p className="text-sm text-muted-foreground">
                  {i18n.language === 'bg' ? member.position_bg : member.position}
                </p>
              </motion.div>
            ))}
          </div>

          <div className="max-w-5xl mx-auto mt-12 border-l-4 border-accent bg-card px-6 py-5 md:px-8 md:py-6">
            <p className="text-base md:text-lg leading-relaxed text-foreground">
              {i18n.language === 'bg'
                ? 'Поради кончината на Борислав Михайлов, Управителният съвет на Фондацията избра за негов заместник като представител Пламен Николов.'
                : 'Following the passing of Borislav Mihaylov, the Foundation’s Management Board elected Plamen Nikolov to succeed him as its representative.'}
            </p>
          </div>
        </div>
      </section>

      <section className="py-20 bg-background">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-20">
          <motion.h2
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="font-heading text-4xl md:text-5xl font-bold text-foreground mb-4 text-center"
            data-testid="legends-title"
          >
            {t('about.legendsTitle')}
          </motion.h2>
          <p className="text-center text-muted-foreground mb-8">
            {i18n.language === 'bg' ? 'Формация 4-4-2 • Кликни за повече информация' : 'Formation 4-4-2 • Click for more info'}
          </p>

          {starters.length >= 11 ? (
            <div className="max-w-4xl mx-auto">
              <div 
                className="relative p-4 sm:p-6 md:p-8 rounded-lg overflow-hidden"
                style={{ 
                  background: 'linear-gradient(to bottom, #2d8a4e 0%, #1e6b3a 50%, #1a5c32 100%)',
                  boxShadow: 'inset 0 0 100px rgba(0,0,0,0.3)'
                }}
              >
                {/* Field markings */}
                <div className="absolute inset-0 pointer-events-none">
                  <div className="absolute top-1/2 left-0 right-0 h-0.5 bg-white/20" />
                  <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-20 h-20 md:w-28 md:h-28 border-2 border-white/20 rounded-full" />
                  <div className="absolute top-0 left-1/2 -translate-x-1/2 w-32 md:w-48 h-12 md:h-16 border-2 border-t-0 border-white/20" />
                  <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-32 md:w-48 h-12 md:h-16 border-2 border-b-0 border-white/20" />
                </div>
                
                {/* Goalkeeper */}
                <div className="flex justify-center mb-4 sm:mb-6 md:mb-8 relative z-10">
                  {goalkeeper.map(player => (
                    <PlayerCard key={player.id} player={player} />
                  ))}
                </div>

                {/* Defenders - 4 */}
                <div className="grid grid-cols-4 gap-1 sm:gap-2 md:gap-4 mb-4 sm:mb-6 md:mb-8 relative z-10 max-w-3xl mx-auto">
                  {defenders.map(player => (
                    <PlayerCard key={player.id} player={player} />
                  ))}
                </div>

                {/* Midfielders - 4 */}
                <div className="grid grid-cols-4 gap-1 sm:gap-2 md:gap-4 mb-4 sm:mb-6 md:mb-8 relative z-10 max-w-3xl mx-auto">
                  {midfielders.map(player => (
                    <PlayerCard key={player.id} player={player} />
                  ))}
                </div>

                {/* Forwards - 2 */}
                <div className="flex justify-center gap-8 sm:gap-12 md:gap-20 relative z-10">
                  {forwards.map(player => (
                    <PlayerCard key={player.id} player={player} />
                  ))}
                </div>
              </div>
              
                            {/* Coaches Section */}
{coaches.length > 0 && (
  <div className="mt-20">
    <h3 className="font-heading text-2xl md:text-3xl font-bold text-foreground mb-2 text-center">
      {i18n.language === 'bg' ? 'Треньорски щаб' : 'Coaching Staff'}
    </h3>
    <p className="text-center text-muted-foreground mb-8">
      {i18n.language === 'bg' ? 'Кликни за повече информация' : 'Click for more info'}
    </p>
    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6 max-w-4xl mx-auto">
      {coaches.map(coach => (
        <div 
          key={coach.id} 
          className="cursor-pointer group"
          data-testid={`coach-${coach.id}`}
          onClick={() => setSelectedPlayer(coach)}
        >
          <div className="aspect-square overflow-hidden border-2 border-border bg-card group-hover:border-accent transition-all duration-300">
            <img
              src={coach.image}
              alt={i18n.language === 'bg' ? coach.name_bg : coach.name}
              className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
            />
          </div>
          <div className="mt-4 text-center">
            <h4 className="font-heading text-lg font-semibold text-foreground group-hover:text-accent transition-colors">
              {i18n.language === 'bg' ? coach.name_bg : coach.name}
            </h4>
            <p className="text-sm text-muted-foreground">
              {i18n.language === 'bg' ? coach.position_bg : coach.position}
            </p>
            <p className="text-xs text-muted-foreground/70 mt-1 line-clamp-2">
              {i18n.language === 'bg' ? coach.bio_bg : coach.bio}
            </p>
          </div>
        </div>
      ))}
    </div>
  </div>
)}
              {/* Reserves Section */}
              {reserves.length > 0 && (
                <div className="mt-16">
                  <h3 className="font-heading text-2xl md:text-3xl font-bold text-foreground mb-2 text-center">
                    {i18n.language === 'bg' ? 'Резерви' : 'Reserves'}
                  </h3>
                  <p className="text-center text-muted-foreground mb-8">
                    {i18n.language === 'bg' ? 'Кликни за повече информация' : 'Click for more info'}
                  </p>
                  <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-4 max-w-4xl mx-auto">
                    {reserves.map(player => (
                      <div 
                        key={player.id} 
                        className="cursor-pointer group"
                        data-testid={`reserve-${player.id}`}
                        onClick={() => setSelectedPlayer(player)}
                      >
                        <div className="aspect-square overflow-hidden border-2 border-border bg-card group-hover:border-accent transition-all duration-300">
                          <img
                            src={player.image}
                            alt={i18n.language === 'bg' ? player.name_bg : player.name}
                            className="w-full h-full object-cover grayscale group-hover:grayscale-0 transition-all duration-500 group-hover:scale-110"
                          />
                        </div>
                        <div className="mt-2 text-center">
                          <h4 className="font-heading text-xs sm:text-sm font-semibold text-foreground truncate">
                            {i18n.language === 'bg' ? player.name_bg : player.name}
                          </h4>
                          <p className="text-[10px] sm:text-xs text-muted-foreground">#{player.number}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="text-center py-20">
              <p className="text-muted-foreground text-lg">
                {i18n.language === 'bg' ? 'Зареждане на отбора...' : 'Loading team...'}
              </p>
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default AboutPage;
